import { api } from '../../api/client';
import { sedrApi } from '../../api/sedr';
import { getAppState } from '../../app';
import {
  generateSedrRoomKey,
  wrapSedrRoomKeyForCreateInvitee,
  wrapSedrRoomKeyForCreator,
} from '../../crypto/sedrRoomKey';
import { applyI18n, t } from '../../i18n';
import { bindLocaleRefresh } from '../../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../../help/dialogHelpTopics';
import { bindModalDismiss, createModalShell } from '../../ui/modalDialogShell';
import { showSedrFolderStarterSetPreviewDialog } from './sedrFolderStarterSetPreviewDialog';
import { sedrErrorMessageKey } from './sedrErrors';
import { showSedrStarterSetPreviewDialog } from './sedrStarterSetImportDialog';

export function showSedrCreateDialog(): Promise<{ id: string } | null> {
  return new Promise((resolve) => {
    const shell = createModalShell({
      titleKey: 'sedr.create.title',
      titleId: 'sedr-create-dialog-title',
      helpTopic: dialogHelpTopic('sedrCreate'),
    });

    shell.body.innerHTML = `
      <form class="form-grid" id="sedr-create-dialog-form">
        <div class="sedr-create-step" id="sedr-create-step-basics">
          <p class="muted" data-i18n="sedr.create.subtitle"></p>
          <label>
            <span data-i18n="sedr.create.titleLabel"></span>
            <input type="text" name="title" maxlength="200" required autocomplete="off" />
            <span class="muted" data-i18n="sedr.create.titlePlaceholder"></span>
          </label>
          <label>
            <span data-i18n="sedr.create.clientLabel"></span>
            <input type="text" name="clientOrganization" maxlength="200" required autocomplete="off" />
            <span class="muted" data-i18n="sedr.create.clientPlaceholder"></span>
          </label>
          <label>
            <span data-i18n="sedr.create.roomTypeLabel"></span>
            <select name="roomType" required>
              <option value="ExternalAudit" data-i18n="sedr.roomType.ExternalAudit"></option>
              <option value="InternalAudit" data-i18n="sedr.roomType.InternalAudit"></option>
              <option value="Checklist" data-i18n="sedr.roomType.Checklist"></option>
            </select>
            <span class="muted" data-i18n="sedr.create.roomTypeHint"></span>
          </label>
          <label>
            <span data-i18n="sedr.create.secondAdminLabel"></span>
            <select name="secondAdminUserId">
              <option value="" data-i18n="sedr.create.secondAdminNone"></option>
            </select>
            <span class="muted" data-i18n="sedr.create.secondAdminHint"></span>
          </label>
        </div>
        <div class="sedr-create-step" id="sedr-create-step-options" hidden>
          <label class="checkbox-row" id="sedr-create-allow-guest-links-row">
            <input type="checkbox" name="allowGuestLinks" checked />
            <span>
              <span data-i18n="sedr.create.allowGuestLinks"></span>
              <span class="muted" data-i18n="sedr.create.allowGuestLinksHint"></span>
            </span>
          </label>
          <label class="checkbox-row" id="sedr-create-require-guest-totp-row">
            <input type="checkbox" name="requireGuestTotp" />
            <span>
              <span data-i18n="sedr.create.requireGuestTotp"></span>
              <span class="muted" data-i18n="sedr.create.requireGuestTotpHint"></span>
            </span>
          </label>
          <label>
            <span data-i18n="sedr.create.requirementsSetLabel"></span>
            <div class="sedr-create-set-picker">
              <select name="requirementsStarterSetId" id="sedr-create-requirements-set">
                <option value="" data-i18n="sedr.create.starterSetNone"></option>
              </select>
              <button
                type="button"
                class="button subtle"
                id="sedr-create-requirements-preview"
                data-i18n="sedr.import.preview"
                disabled
              ></button>
            </div>
            <span class="muted" data-i18n="sedr.create.requirementsSetHint"></span>
          </label>
          <label>
            <span data-i18n="sedr.create.folderSetLabel"></span>
            <div class="sedr-create-set-picker">
              <select name="folderStarterSetId" id="sedr-create-folder-set">
                <option value="" data-i18n="sedr.create.starterSetNone"></option>
              </select>
              <button
                type="button"
                class="button subtle"
                id="sedr-create-folder-preview"
                data-i18n="sedr.import.preview"
                disabled
              ></button>
            </div>
            <span class="muted" data-i18n="sedr.create.folderSetHint"></span>
          </label>
          <p class="error-text" id="sedr-create-dialog-error" hidden></p>
        </div>
      </form>
    `;

    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.className = 'button ghost';
    cancelButton.id = 'sedr-create-dialog-cancel';
    cancelButton.innerHTML = '<span data-i18n="common.cancel"></span>';

    const backButton = document.createElement('button');
    backButton.type = 'button';
    backButton.className = 'button ghost';
    backButton.id = 'sedr-create-dialog-back';
    backButton.hidden = true;
    backButton.innerHTML = '<span data-i18n="common.back"></span>';

    const nextButton = document.createElement('button');
    nextButton.type = 'button';
    nextButton.className = 'button primary';
    nextButton.id = 'sedr-create-dialog-next';
    nextButton.innerHTML = '<span data-i18n="common.next"></span>';

    const submitButton = document.createElement('button');
    submitButton.type = 'submit';
    submitButton.className = 'button primary';
    submitButton.id = 'sedr-create-dialog-submit';
    submitButton.setAttribute('form', 'sedr-create-dialog-form');
    submitButton.hidden = true;
    submitButton.innerHTML = '<span data-i18n="sedr.list.create"></span>';

    shell.actions.append(cancelButton, backButton, nextButton, submitButton);
    document.body.append(shell.backdrop);
    applyI18n(shell.dialog);

    const form = shell.body.querySelector('#sedr-create-dialog-form') as HTMLFormElement;
    const stepBasics = form.querySelector('#sedr-create-step-basics') as HTMLElement;
    const stepOptions = form.querySelector('#sedr-create-step-options') as HTMLElement;
    const errorEl = form.querySelector('#sedr-create-dialog-error') as HTMLParagraphElement;
    const adminSelect = form.querySelector('select[name="secondAdminUserId"]') as HTMLSelectElement;
    const roomTypeSelect = form.querySelector('select[name="roomType"]') as HTMLSelectElement;
    const allowGuestLinksRow = form.querySelector('#sedr-create-allow-guest-links-row') as HTMLLabelElement;
    const allowGuestLinksInput = form.querySelector('input[name="allowGuestLinks"]') as HTMLInputElement;
    const requireGuestTotpRow = form.querySelector('#sedr-create-require-guest-totp-row') as HTMLLabelElement;
    const requireGuestTotpInput = form.querySelector('input[name="requireGuestTotp"]') as HTMLInputElement;
    const titleInput = form.querySelector('input[name="title"]') as HTMLInputElement;
    let submitting = false;
    let lastErrorKey = 'status.error';
    let step: 1 | 2 = 1;

    const syncGuestOptions = () => {
      const external = roomTypeSelect.value === 'ExternalAudit';
      allowGuestLinksRow.hidden = external;
      if (external) {
        allowGuestLinksInput.checked = true;
      }

      const guestsEnabled = external || allowGuestLinksInput.checked;
      requireGuestTotpRow.hidden = !guestsEnabled;
      if (!guestsEnabled) {
        requireGuestTotpInput.checked = false;
      }
    };

    const showStep = (next: 1 | 2) => {
      step = next;
      stepBasics.hidden = next !== 1;
      stepOptions.hidden = next !== 2;
      cancelButton.hidden = next !== 1;
      nextButton.hidden = next !== 1;
      backButton.hidden = next !== 2;
      submitButton.hidden = next !== 2;

      if (next === 2) {
        syncGuestOptions();
        errorEl.hidden = true;
        const focusTarget = Array.from(
          stepOptions.querySelectorAll<HTMLElement>('input, select, textarea, button'),
        ).find((el) => {
          const label = el.closest('label');
          return !(label?.hidden || el.hidden);
        });
        focusTarget?.focus();
      } else {
        titleInput.focus();
      }
    };

    roomTypeSelect.addEventListener('change', () => {
      if (step === 2) syncGuestOptions();
    });
    allowGuestLinksInput.addEventListener('change', syncGuestOptions);

    const refreshLocale = () => {
      if (!errorEl.hidden) {
        errorEl.textContent = t(lastErrorKey);
      }
      applyI18n(shell.dialog);
    };
    const unbindLocale = bindLocaleRefresh(refreshLocale);

    const close = (result: { id: string } | null) => {
      if (submitting) return;
      unbindLocale();
      unbindDismiss();
      shell.backdrop.remove();
      resolve(result);
    };

    const unbindDismiss = bindModalDismiss({
      backdrop: shell.backdrop,
      closeButton: shell.closeButton,
      onClose: () => close(null),
    });

    cancelButton.addEventListener('click', () => close(null));
    backButton.addEventListener('click', () => {
      if (submitting) return;
      showStep(1);
    });
    nextButton.addEventListener('click', () => {
      if (submitting) return;
      if (!form.reportValidity()) return;
      showStep(2);
    });

    void (async () => {
      try {
        const state = getAppState();
        const currentUserId = state.kind === 'authenticated' ? state.session.userId : '';
        const directory = await api.getKeyDirectory();
        for (const entry of directory.filter((e) => e.userId !== currentUserId)) {
          const option = document.createElement('option');
          option.value = entry.userId;
          option.textContent = entry.displayName;
          adminSelect.append(option);
        }
      } catch {
        // Directory load failure still allows create without second admin.
      }

      const requirementsSelect = form.querySelector('#sedr-create-requirements-set') as HTMLSelectElement;
      const folderSelect = form.querySelector('#sedr-create-folder-set') as HTMLSelectElement;
      const requirementsPreviewBtn = form.querySelector(
        '#sedr-create-requirements-preview',
      ) as HTMLButtonElement;
      const folderPreviewBtn = form.querySelector('#sedr-create-folder-preview') as HTMLButtonElement;

      const syncPreviewButtons = () => {
        requirementsPreviewBtn.disabled = !requirementsSelect.value;
        folderPreviewBtn.disabled = !folderSelect.value;
      };

      requirementsSelect.addEventListener('change', syncPreviewButtons);
      folderSelect.addEventListener('change', syncPreviewButtons);

      requirementsPreviewBtn.addEventListener('click', () => {
        const option = requirementsSelect.selectedOptions[0];
        const setId = requirementsSelect.value.trim();
        if (!setId || !option) return;
        showSedrStarterSetPreviewDialog({
          setId,
          setName: option.dataset.name || option.textContent || setId,
        });
      });

      folderPreviewBtn.addEventListener('click', () => {
        const option = folderSelect.selectedOptions[0];
        const setId = folderSelect.value.trim();
        if (!setId || !option) return;
        showSedrFolderStarterSetPreviewDialog({
          setId,
          setName: option.dataset.name || option.textContent || setId,
        });
      });

      try {
        const [requirementsSets, folderSets] = await Promise.all([
          sedrApi.listStarterSets('Requirements'),
          sedrApi.listStarterSets('Folders'),
        ]);
        for (const set of requirementsSets) {
          const option = document.createElement('option');
          option.value = set.id;
          option.dataset.name = set.name;
          option.textContent = `${set.name} (${set.itemCount})`;
          requirementsSelect.append(option);
        }
        for (const set of folderSets) {
          const option = document.createElement('option');
          option.value = set.id;
          option.dataset.name = set.name;
          option.textContent = `${set.name} (${set.itemCount})`;
          folderSelect.append(option);
        }
      } catch {
        // Starter set load failure still allows create without templates.
      }
      syncPreviewButtons();
      applyI18n(form);
      titleInput.focus();
    })();

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (submitting) return;
      if (step === 1) {
        if (!form.reportValidity()) return;
        showStep(2);
        return;
      }

      void (async () => {
        submitting = true;
        errorEl.hidden = true;
        submitButton.disabled = true;
        backButton.disabled = true;

        try {
          const data = new FormData(form);
          const title = String(data.get('title') ?? '').trim();
          const clientOrganization = String(data.get('clientOrganization') ?? '').trim();
          const secondAdminUserId = String(data.get('secondAdminUserId') ?? '').trim();
          if (!title || !clientOrganization) {
            submitting = false;
            submitButton.disabled = false;
            backButton.disabled = false;
            showStep(1);
            form.reportValidity();
            return;
          }

          const roomKey = await generateSedrRoomKey();
          const wrappedRoomKey = await wrapSedrRoomKeyForCreator(roomKey);
          const requireGuestTotp = data.get('requireGuestTotp') === 'on';
          const roomType = String(data.get('roomType') ?? 'ExternalAudit');
          const allowGuestLinks = roomType === 'ExternalAudit' || data.get('allowGuestLinks') === 'on';
          const requirementsStarterSetId = String(data.get('requirementsStarterSetId') ?? '').trim() || null;
          const folderStarterSetId = String(data.get('folderStarterSetId') ?? '').trim() || null;
          const created = await sedrApi.createRoom({
            title,
            clientOrganization,
            wrappedRoomKey,
            roomType,
            requireGuestTotp,
            allowGuestLinks,
            requirementsStarterSetId,
            folderStarterSetId,
            ...(secondAdminUserId
              ? {
                  secondAdminUserId,
                  secondAdminWrappedRoomKey: await wrapSedrRoomKeyForCreateInvitee(
                    roomKey,
                    secondAdminUserId,
                  ),
                }
              : {}),
          });
          submitting = false;
          unbindLocale();
          unbindDismiss();
          shell.backdrop.remove();
          resolve({ id: created.id });
        } catch (cause) {
          submitting = false;
          submitButton.disabled = false;
          backButton.disabled = false;
          lastErrorKey = sedrErrorMessageKey(cause);
          errorEl.hidden = false;
          errorEl.textContent = t(lastErrorKey);
        }
      })();
    });
  });
}
